import {
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { activityPost } from "@/db/schema/activity-post";
import { user } from "@/db/schema/auth";

export const VERIFICATION_CASE_KIND = [
  "activity_session_result",
  "activity_post_candidate",
] as const;
export type VerificationCaseKind = (typeof VERIFICATION_CASE_KIND)[number];

export const VERIFICATION_CASE_STATUS = [
  "pending",
  "verified",
  "disputed",
  "expired",
] as const;
export type VerificationCaseStatus = (typeof VERIFICATION_CASE_STATUS)[number];

export const VERIFICATION_CASE_CONFIRMATION_DECISION = [
  "confirmed",
  "disputed",
] as const;
export type VerificationCaseConfirmationDecision =
  (typeof VERIFICATION_CASE_CONFIRMATION_DECISION)[number];

export const verificationCase = sqliteTable(
  "verification_case",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    kind: text("kind").notNull().$type<VerificationCaseKind>(),

    status: text("status")
      .notNull()
      .$type<VerificationCaseStatus>()
      .default("pending"),

    revision: integer("revision").notNull().default(1),

    expiresAt: integer("expires_at", { mode: "timestamp" }),
    lastManualReminderAt: integer("last_manual_reminder_at", {
      mode: "timestamp",
    }),

    resolvedByUserId: text("resolved_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    resolvedAt: integer("resolved_at", { mode: "timestamp" }),

    publishedActivityPostId: integer("published_activity_post_id").references(
      () => activityPost.id,
      { onDelete: "set null" },
    ),
    publishedByUserId: text("published_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    publishedAt: integer("published_at", { mode: "timestamp" }),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    index("verification_case_kind_idx").on(t.kind),
    index("verification_case_status_idx").on(t.status),
    index("verification_case_expires_idx").on(t.expiresAt),
    uniqueIndex("verification_case_published_activity_post_unique").on(
      t.publishedActivityPostId,
    ),
  ],
);

export type VerificationCase = typeof verificationCase.$inferSelect;
export type VerificationCaseInsert = typeof verificationCase.$inferInsert;

export const verificationCaseRevision = sqliteTable(
  "verification_case_revision",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    caseId: integer("case_id")
      .notNull()
      .references(() => verificationCase.id, { onDelete: "cascade" }),

    revision: integer("revision").notNull(),

    submittedByUserId: text("submitted_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    payload: text("payload", { mode: "json" }).$type<Record<string, unknown>>(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("verification_case_revision_unique").on(t.caseId, t.revision),
    index("verification_case_revision_case_idx").on(t.caseId),
    index("verification_case_revision_submitter_idx").on(t.submittedByUserId),
  ],
);

export type VerificationCaseRevision =
  typeof verificationCaseRevision.$inferSelect;
export type VerificationCaseRevisionInsert =
  typeof verificationCaseRevision.$inferInsert;

export const verificationCaseParticipant = sqliteTable(
  "verification_case_participant",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    caseId: integer("case_id")
      .notNull()
      .references(() => verificationCase.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    role: text("role"),
    team: text("team"),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("verification_case_participant_unique").on(t.caseId, t.userId),
    index("verification_case_participant_case_idx").on(t.caseId),
    index("verification_case_participant_user_idx").on(t.userId),
  ],
);

export type VerificationCaseParticipant =
  typeof verificationCaseParticipant.$inferSelect;
export type VerificationCaseParticipantInsert =
  typeof verificationCaseParticipant.$inferInsert;

export const verificationCaseConfirmation = sqliteTable(
  "verification_case_confirmation",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    caseId: integer("case_id")
      .notNull()
      .references(() => verificationCase.id, { onDelete: "cascade" }),

    revision: integer("revision").notNull(),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    decision: text("decision")
      .notNull()
      .$type<VerificationCaseConfirmationDecision>(),

    reason: text("reason"),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("verification_case_confirmation_unique").on(
      t.caseId,
      t.revision,
      t.userId,
    ),
    index("verification_case_confirmation_case_idx").on(t.caseId, t.revision),
    index("verification_case_confirmation_user_idx").on(t.userId),
    index("verification_case_confirmation_decision_idx").on(t.decision),
  ],
);

export type VerificationCaseConfirmation =
  typeof verificationCaseConfirmation.$inferSelect;
export type VerificationCaseConfirmationInsert =
  typeof verificationCaseConfirmation.$inferInsert;

export const verificationCaseTimeline = sqliteTable(
  "verification_case_timeline",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    caseId: integer("case_id")
      .notNull()
      .references(() => verificationCase.id, { onDelete: "cascade" }),

    type: text("type").notNull(),
    content: text("content").notNull(),
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    actorId: text("actor_id").references(() => user.id, {
      onDelete: "set null",
    }),

    ...timestamps,
  },
  (t) => [
    index("verification_case_timeline_case_idx").on(t.caseId),
    index("verification_case_timeline_created_idx").on(t.createdAt),
  ],
);

export type VerificationCaseTimeline =
  typeof verificationCaseTimeline.$inferSelect;
export type VerificationCaseTimelineInsert =
  typeof verificationCaseTimeline.$inferInsert;
